import { useEffect, useState } from "react";
import { Instagram, ArrowUpRight, Play } from "lucide-react";
import { motion } from "framer-motion";
import { Eyebrow, MaskLines, EASE } from "./Motion";
import { getInstagramFeed } from "@/lib/api";

export const INSTAGRAM_URL = "https://www.instagram.com/jaisunhouse.irel/";
export const INSTAGRAM_HANDLE = "@jaisunhouse.irel";

const TILES = [
  "/assets/GUESSAW26WW4.jpg",
  "/assets/HESKIMOAW266.jpg",
  "/assets/Cam-kids.jpg",
  "/assets/TIFFOSI-263.jpg",
  "/assets/HS1.jpg",
  "/assets/joop-men.jpg",
].map((src) => ({ id: src, image: src, permalink: INSTAGRAM_URL, caption: "Jaisun House on Instagram" }));

export const useInstagramFeed = (account) => {
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    let alive = true;
    getInstagramFeed(account).then((f) => {
      if (alive) setFeed(f);
    });
    return () => {
      alive = false;
    };
  }, [account]);
  return feed;
};

export const gridFor = (posts) => {
  const n = posts.length;
  if (n >= 6) return { list: posts.slice(0, 6), cls: "grid-cols-3 lg:grid-cols-6" };
  if (n >= 4) return { list: posts.slice(0, 4), cls: "grid-cols-2 lg:grid-cols-4" };
  if (n === 3) return { list: posts, cls: "grid-cols-3" };
  return { list: posts, cls: "grid-cols-2" };
};

export const Tile = ({ post, i, testid = "instagram-tile" }) => (
  <motion.a
    href={post.permalink}
    target="_blank"
    rel="noreferrer"
    data-testid={`${testid}-${i}`}
    data-cursor="view"
    className="group relative block aspect-square overflow-hidden bg-[#121418]"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.9, ease: EASE, delay: i * 0.06 }}
  >
    <img src={post.image} alt={post.caption?.slice(0, 120) || "Instagram post"} loading="lazy" className="h-full w-full object-cover object-top grayscale-[30%] transition-[transform,filter] duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110 group-hover:grayscale-0" />
    {post.media_type === "VIDEO" && <Play data-testid={`${testid}-${i}-video-icon`} className="absolute right-3 top-3 h-5 w-5 fill-[#f5f3ef] text-[#f5f3ef] drop-shadow" />}
    <span className="absolute inset-0 flex items-center justify-center bg-[#0a0b0d]/0 transition-colors duration-500 group-hover:bg-[#0a0b0d]/45">
      <Instagram className="h-7 w-7 scale-75 text-[#f5f3ef] opacity-0 transition-[opacity,transform] duration-500 group-hover:scale-100 group-hover:opacity-100" />
    </span>
  </motion.a>
);

export const InstagramSection = () => {
  const feed = useInstagramFeed("house");
  const posts = feed?.posts ?? TILES;
  const grid = gridFor(posts);
  return (
  <section data-testid="instagram-section" data-live={feed ? "true" : "false"} className="border-t border-white/10 bg-[#0a0b0d]">
    <div className="mx-auto max-w-[1600px] px-6 py-24 sm:px-10 sm:py-32 lg:px-14">
      <div className="mb-14 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-8">
          <Eyebrow index="07">Social</Eyebrow>
          <h2 className="mt-8 font-display text-6xl leading-[0.9] sm:text-7xl lg:text-8xl">
            <MaskLines inView lines={["Follow us on", <em key="i" className="text-[#c8a97e]">Instagram.</em>]} />
          </h2>
        </div>
        <div className="lg:col-span-4">
          <p className="text-base leading-relaxed text-[#c9c6c0]">New-season campaigns, showroom moments and brand news from Dundrum.</p>
          <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="instagram-follow-btn" className="group mt-8 inline-flex items-center gap-3 rounded-full border border-[#c8a97e]/60 px-6 py-3.5 text-[13px] uppercase tracking-[0.16em] text-[#c8a97e] transition-colors duration-300 hover:bg-[#c8a97e] hover:text-[#0a0b0d]">
            <Instagram className="h-4 w-4" /> {INSTAGRAM_HANDLE}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
          </a>
        </div>
      </div>
      <div className={`grid gap-2 sm:gap-4 ${grid.cls}`}>
        {grid.list.map((p, i) => <Tile key={p.id} post={p} i={i} />)}
      </div>
    </div>
  </section>
  );
};
